<script setup>
/**
 * AppTable — universelle Vollseiten-Tabelle
 *
 * Props:
 *   tableKey     String   Eindeutiger Schlüssel → localStorage-Persistence
 *   columns      Array    [{ field, header, sortable?, default?, minWidth? }]
 *   data         Array    Zeilen (client-side) oder aktuell geladene Seite (lazy)
 *   total        Number   Gesamtanzahl (nur relevant bei lazy=true)
 *   loading      Boolean
 *   lazy         Boolean  true → server-side (emit query-change)
 *   searchable   Boolean  Suchfeld anzeigen (default: true)
 *   actions      Array    Aktions-Definitionen für den Aktionen-Dropdown:
 *                         [{ label, icon, severity?, requiresSingle?, separator?,
 *                            command(selectedRows) }]
 *                         requiresSingle: true  → disabled wenn ≠1 ausgewählt (Edit)
 *                         ohne flag            → disabled wenn 0 ausgewählt (Delete)
 *                         severity: 'danger'   → rote Schrift im Menü
 *                         separator: true      → Trennlinie davor
 *   emptyText    String
 *   defaultRows  Number   Standard-Seitengröße (default: 25)
 *   rowsOptions  Array    Verfügbare Seitengrößen
 *   dataKey      String   PK-Feld (default: 'id')
 *   rowClickable Boolean  Zeilen-Hover-Cursor (default: false)
 *   expandable   Boolean  Row-Expansion aktivieren (default: false)
 *
 * Emits:
 *   refresh          → Tabelle neu laden
 *   query-change     → { search, page, limit, sortField, sortOrder }  (nur lazy)
 *   row-click        → row object
 *
 * Slots:
 *   toolbar          → rechte Seite der Toolbar (z.B. "+ Neu"-Button, Dropdowns)
 *   toolbar-left     → linke Seite der Toolbar
 *   cell-{field}     → custom Zell-Rendering, erhält { row }
 *   expansion        → Expansion-Inhalt, erhält { row }  (nur wenn expandable=true)
 *   empty            → eigener Leer-Zustand
 */
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useI18n } from '@/i18n'
import DataTable from 'primevue/datatable'
import Column    from 'primevue/column'
import InputText from 'primevue/inputtext'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import Button    from 'primevue/button'
import Checkbox  from 'primevue/checkbox'
import Popover   from 'primevue/popover'
import Menu      from 'primevue/menu'

const props = defineProps({
  tableKey:    { type: String,  required: true },
  columns:     { type: Array,   required: true },
  data:        { type: Array,   default: () => [] },
  total:       { type: Number,  default: 0 },
  loading:     { type: Boolean, default: false },
  lazy:        { type: Boolean, default: false },
  searchable:  { type: Boolean, default: true },
  actions:     { type: Array,   default: () => [] },
  emptyText:   { type: String,  default: '' },
  defaultRows: { type: Number,  default: 25 },
  rowsOptions: { type: Array,   default: () => [10, 25, 50, 100] },
  dataKey:          { type: String,  default: 'id' },
  rowClickable:     { type: Boolean, default: false },
  expandable:       { type: Boolean, default: false },
  defaultSortField: { type: String,  default: null },
  defaultSortOrder: { type: String,  default: null },
  /* Funktion (row) => string|object — zusätzliche CSS-Klasse(n) je Zeile */
  rowClass:         { type: Function, default: null },
  /* Suchfeld beim Mounten automatisch fokussieren */
  autofocusSearch:  { type: Boolean, default: false },
  /* Außenrahmen + Eck-Radius entfernen (z.B. eingebettet in hw-card/Dialog) */
  borderless:       { type: Boolean, default: false },
})

const { t } = useI18n()
const emit  = defineEmits(['refresh', 'query-change', 'row-click'])

// ─── Persistence ────────────────────────────────────────────────────────────
const PREF_KEY = computed(() => `tablePrefs:${props.tableKey}`)

function buildDefaults() {
  return Object.fromEntries(props.columns.map(c => [c.field, c.default !== false]))
}

const colVisibility = ref({})
const rowsPerPage   = ref(props.defaultRows)

const searchInputRef = ref(null)

onMounted(() => {
  try {
    const stored = JSON.parse(localStorage.getItem(PREF_KEY.value) || 'null')
    colVisibility.value = stored ? { ...buildDefaults(), ...stored.cols } : buildDefaults()
    if (stored?.rows && props.rowsOptions.includes(stored.rows)) rowsPerPage.value = stored.rows
  } catch {
    colVisibility.value = buildDefaults()
  }
  if (props.autofocusSearch && props.searchable) {
    nextTick(() => setTimeout(() => searchInputRef.value?.$el?.focus(), 120))
  }
})

watch([colVisibility, rowsPerPage], () => {
  try {
    localStorage.setItem(PREF_KEY.value, JSON.stringify({
      cols: colVisibility.value,
      rows: rowsPerPage.value,
    }))
  } catch { /* storage voll */ }
}, { deep: true })

const visibleColumns = computed(() =>
  props.columns.filter(c => colVisibility.value[c.field] !== false)
)
const hasHiddenCols = computed(() =>
  props.columns.some(c => colVisibility.value[c.field] === false)
)

// ─── Suche ──────────────────────────────────────────────────────────────────
const search = ref('')
let searchTimer = null

watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(() => {
    dtFirst.value = 0
    if (props.lazy) emitQuery()
  }, 300)
})

const filtered = computed(() => {
  if (props.lazy) return props.data
  const term = search.value.trim().toLowerCase()
  if (!term) return props.data
  return props.data.filter(row =>
    props.columns.some(c => String(row[c.field] ?? '').toLowerCase().includes(term))
  )
})

// ─── Paginierung / Sortierung ────────────────────────────────────────────────
const dtFirst   = ref(0)
const sortField = ref(props.defaultSortField ?? null)
const sortOrder = ref(props.defaultSortOrder ?? null)

function onPage(e) {
  dtFirst.value     = e.first
  rowsPerPage.value = e.rows
  if (props.lazy) emitQuery()
}

function onSort(e) {
  sortField.value = e.sortField ?? null
  sortOrder.value = e.sortOrder === 1 ? 'asc' : (e.sortOrder === -1 ? 'desc' : null)
  dtFirst.value   = 0
  if (props.lazy) emitQuery()
}

function emitQuery() {
  emit('query-change', {
    search:    search.value.trim(),
    page:      Math.floor(dtFirst.value / rowsPerPage.value) + 1,
    limit:     rowsPerPage.value,
    sortField: sortField.value,
    sortOrder: sortOrder.value,
  })
}

/**
 * reload(): auf Seite 1 zurücksetzen und neu laden.
 * Für externe Filter (z.B. Kategorie-Dropdown im toolbar-Slot) gedacht —
 * die Eltern-Komponente liest ihren Filter-State im query-change-Handler.
 */
function reload({ resetSearch = false } = {}) {
  if (resetSearch) search.value = ''
  dtFirst.value = 0
  if (props.lazy) emitQuery()
}

const totalRecords = computed(() => props.lazy ? props.total : filtered.value.length)

// ─── Zeilen-Klasse ───────────────────────────────────────────────────────────
function computeRowClass(row) {
  const classes = []
  if (props.rowClickable) classes.push('app-table-row-clickable')
  if (props.rowClass) {
    const extra = props.rowClass(row)
    if (extra) classes.push(extra)
  }
  return classes.length ? classes.join(' ') : undefined
}

// ─── Spalten-Picker ──────────────────────────────────────────────────────────
const pickerRef = ref(null)
function togglePicker(e) { pickerRef.value?.toggle(e) }
function resetCols()      { colVisibility.value = buildDefaults() }

// ─── Auswahl & Aktionen ──────────────────────────────────────────────────────
const selectedRows   = ref([])
const expandedRows   = ref([])
const actionsMenuRef = ref(null)

const computedActions = computed(() => props.actions.filter(Boolean))
const hasActions      = computed(() => computedActions.value.length > 0)

function itemDisabled(a) {
  if (a.requiresSingle) return selectedRows.value.length !== 1
  return selectedRows.value.length === 0
}

const menuItems = computed(() => {
  const items = []
  for (const a of computedActions.value) {
    if (a.separator && items.length) items.push({ separator: true })
    items.push({
      label:    a.label,
      icon:     a.icon,
      class:    a.severity === 'danger' ? 'action-item-danger' : undefined,
      disabled: itemDisabled(a),
      command:  () => {
        a.command(selectedRows.value)
        selectedRows.value = []
      },
    })
  }
  return items
})

// Auswahl + Expansion bei Datenwechsel zurücksetzen
watch(() => props.data, () => { selectedRows.value = []; expandedRows.value = [] })

// ─── Export ─────────────────────────────────────────────────────────────────
const exportMenuRef = ref(null)

const exportItems = computed(() => [
  { label: 'CSV',  icon: 'pi pi-file',  command: () => doExport('csv')  },
  { label: 'JSON', icon: 'pi pi-code',  command: () => doExport('json') },
])

function getExportRows() {
  return props.lazy ? props.data : filtered.value
}

function doExport(format) {
  const rows     = getExportRows()
  const cols     = visibleColumns.value
  const filename = `${props.tableKey}-${new Date().toISOString().slice(0, 10)}`

  if (format === 'csv') {
    const header = cols.map(c => csvCell(c.header ?? c.field)).join(',')
    const body   = rows.map(row =>
      cols.map(c => csvCell(row[c.field] ?? '')).join(',')
    ).join('\r\n')
    triggerDownload(`${filename}.csv`, '﻿' + header + '\r\n' + body, 'text/csv')
  } else {
    const payload = rows.map(row =>
      Object.fromEntries(cols.map(c => [c.field, row[c.field] ?? null]))
    )
    triggerDownload(`${filename}.json`, JSON.stringify(payload, null, 2), 'application/json')
  }
}

function csvCell(val) {
  const str = String(val).replace(/\n/g, ' ')
  return str.includes(',') || str.includes('"') || str.includes('\r')
    ? '"' + str.replace(/"/g, '""') + '"'
    : str
}

function triggerDownload(filename, content, type) {
  const blob = new Blob([content], { type: `${type};charset=utf-8;` })
  const url  = URL.createObjectURL(blob)
  const a    = Object.assign(document.createElement('a'), { href: url, download: filename })
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Public API ───────────────────────────────────────────────────────────────
defineExpose({ reload })
</script>

<template>
  <div class="app-table" :class="{ 'app-table--borderless': borderless }">
    <!-- ── Toolbar ────────────────────────────────────────────────────────── -->
    <div class="app-table-bar">
      <!-- links: custom slot -->
      <div class="app-table-bar-left">
        <slot name="toolbar-left" />
      </div>

      <!-- rechts -->
      <div class="app-table-bar-right">
        <!-- Aktionen-Dropdown (nur wenn actions übergeben) -->
        <template v-if="hasActions">
          <span v-if="selectedRows.length" class="selection-count">
            {{ selectedRows.length }} {{ t('common.selected') }}
          </span>
          <Button
            icon="pi pi-chevron-down"
            :label="t('common.actions')"
            size="small"
            severity="secondary"
            outlined
            @click="actionsMenuRef?.toggle($event)"
          />
          <Menu ref="actionsMenuRef" :model="menuItems" popup>
            <template #item="{ item, props: iProps }">
              <a v-if="!item.separator" v-bind="iProps.action" :class="['action-menu-item', item.class, { 'p-disabled': item.disabled }]">
                <span :class="item.icon" />
                <span class="ml-2">{{ item.label }}</span>
              </a>
            </template>
          </Menu>
        </template>

        <!-- Suche -->
        <IconField v-if="searchable">
          <InputIcon class="pi pi-search" />
          <InputText
            ref="searchInputRef"
            v-model="search"
            :placeholder="t('common.search')"
            class="app-table-search"
          />
        </IconField>

        <!-- Toolbar-Slot (Neu-Button, Dropdown-Filter etc.) -->
        <slot name="toolbar" />

        <!-- Spalten-Picker -->
        <Button
          icon="pi pi-table"
          v-tooltip.top="t('common.columnPicker')"
          size="small"
          text
          severity="secondary"
          :badge="hasHiddenCols ? '!' : undefined"
          @click="togglePicker"
        />

        <!-- Export -->
        <Button
          icon="pi pi-download"
          v-tooltip.top="lazy ? t('common.exportPage') : t('common.export')"
          size="small"
          text
          severity="secondary"
          @click="exportMenuRef?.toggle($event)"
        />
        <Menu ref="exportMenuRef" :model="exportItems" popup />

        <!-- Refresh -->
        <Button
          icon="pi pi-refresh"
          v-tooltip.top="t('common.refresh')"
          size="small"
          text
          severity="secondary"
          :loading="loading"
          @click="$emit('refresh')"
        />
      </div>
    </div>

    <!-- ── DataTable ──────────────────────────────────────────────────────── -->
    <DataTable
      :value="lazy ? data : filtered"
      :loading="loading"
      :lazy="lazy"
      :totalRecords="totalRecords"
      :dataKey="dataKey"
      v-model:selection="selectedRows"
      v-model:expandedRows="expandedRows"
      :selectionMode="hasActions ? 'multiple' : undefined"
      paginator
      :rows="rowsPerPage"
      :rowsPerPageOptions="rowsOptions"
      :first="dtFirst"
      :sortField="sortField"
      :sortOrder="sortOrder === 'asc' ? 1 : sortOrder === 'desc' ? -1 : 0"
      sortMode="single"
      removableSort
      stripedRows
      scrollable
      responsiveLayout="scroll"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
      currentPageReportTemplate="{first}–{last} von {totalRecords}"
      :rowClass="computeRowClass"
      @page="onPage"
      @sort="onSort"
      @row-click="$emit('row-click', $event.data)"
    >
      <!-- Leer-Zustand -->
      <template #empty>
        <slot name="empty">
          <div class="app-table-empty">
            <i class="pi pi-inbox" />
            <span>{{ emptyText || t('common.empty') }}</span>
          </div>
        </slot>
      </template>

      <!-- Expansion-Spalte -->
      <Column v-if="expandable" expander style="width:3rem;padding-right:0" :exportable="false" />

      <!-- Checkbox-Spalte (nur wenn actions vorhanden) -->
      <Column
        v-if="hasActions"
        selectionMode="multiple"
        style="width:44px;padding-right:0"
        :exportable="false"
      />

      <!-- Dynamische Spalten -->
      <Column
        v-for="col in visibleColumns"
        :key="col.field"
        :field="col.field"
        :header="col.header"
        :sortable="col.sortable ?? false"
        :style="col.minWidth ? `min-width:${col.minWidth}` : ''"
      >
        <template #body="{ data: row }">
          <slot :name="`cell-${col.field}`" :row="row">
            {{ row[col.field] }}
          </slot>
        </template>
      </Column>

      <!-- Row-Expansion-Slot -->
      <template v-if="expandable && $slots.expansion" #expansion="{ data: row }">
        <slot name="expansion" :row="row" />
      </template>
    </DataTable>

    <!-- ── Spalten-Picker Popover ─────────────────────────────────────────── -->
    <Popover ref="pickerRef" class="app-table-picker">
      <div class="picker-header">
        <span class="picker-title">{{ t('common.columns') }}</span>
        <button class="picker-reset" @click="resetCols">{{ t('common.reset') }}</button>
      </div>
      <ul class="picker-list">
        <li v-for="col in columns" :key="col.field" class="picker-item">
          <Checkbox
            v-model="colVisibility[col.field]"
            :binary="true"
            :inputId="`pc-${tableKey}-${col.field}`"
          />
          <label :for="`pc-${tableKey}-${col.field}`" class="picker-label">
            {{ col.header }}
          </label>
        </li>
      </ul>
    </Popover>
  </div>
</template>

<style scoped>
/* ── Wrapper ────────────────────────────────────────────────── */
.app-table {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: var(--p-surface-card, #fff);
  border: 1px solid var(--p-content-border-color);
  border-radius: 8px;
  overflow: hidden;
}
.app-table--borderless {
  border: 0;
  border-radius: 0;
  background: transparent;
}
.app-table--borderless :deep(.p-datatable-paginator-bottom) {
  border-radius: 0;
}

/* ── Toolbar ────────────────────────────────────────────────── */
.app-table-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid var(--p-content-border-color);
  gap: 8px;
  flex-wrap: wrap;
}
.app-table-bar-left,
.app-table-bar-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.app-table-search {
  width: 220px;
  height: 32px;
  font-size: 0.875rem;
}

/* ── Auswahl-Zähler ─────────────────────────────────────────── */
.selection-count {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--p-primary-color);
  padding: 0 4px;
}

/* ── DataTable overrides (scoped via :deep) ──────────────────── */
.app-table :deep(.p-datatable) {
  border: 0 !important;
  border-radius: 0 !important;
}
.app-table :deep(.p-datatable-table-container) {
  border-radius: 0;
}
.app-table :deep(.p-datatable-thead > tr > th) {
  background: var(--p-surface-50, var(--p-surface-ground));
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 10px 12px;
  white-space: nowrap;
}
.app-table :deep(.p-datatable-tbody > tr > td) {
  padding: 9px 12px;
  font-size: 0.875rem;
  vertical-align: middle;
}
.app-table :deep(.p-datatable-paginator-bottom) {
  border-top: 1px solid var(--p-content-border-color);
  border-radius: 0 0 8px 8px;
  padding: 6px 12px;
}
.app-table :deep(.p-paginator) {
  background: transparent;
}
/* Striped + hover */
.app-table :deep(.p-datatable-tbody > tr:nth-child(even)) {
  background: var(--p-surface-50, rgba(0,0,0,.02));
}
.app-table :deep(.p-datatable-tbody > tr:hover) {
  background: color-mix(in srgb, var(--p-primary-color) 6%, transparent) !important;
}
.app-table :deep(.app-table-row-clickable) {
  cursor: pointer;
}

/* Laptop-Dichte (MacBook Air & Co., 1181–1680px): kompaktere Zeilen, damit mehr
   Tabelle auf 900px Höhe passt. Große Monitore (>1680px) bleiben unverändert. */
@media (min-width: 1181px) and (max-width: 1680px) {
  .app-table :deep(.p-datatable-thead > tr > th) { padding: 6px 10px; }
  .app-table :deep(.p-datatable-tbody > tr > td) { padding: 6px 10px; }
  .app-table :deep(.p-datatable-paginator-bottom) { padding: 4px 10px; }
}

/* ── Leer-Zustand ────────────────────────────────────────────── */
.app-table-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 20px;
  color: var(--p-text-muted-color);
  font-size: 0.875rem;
}
.app-table-empty .pi { font-size: 2rem; opacity: 0.3; }

/* ── Spalten-Picker ──────────────────────────────────────────── */
.picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 6px;
  border-bottom: 1px solid var(--p-content-border-color);
}
.picker-title {
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--p-text-muted-color);
}
.picker-reset {
  font-size: 0.75rem;
  color: var(--p-primary-color);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.picker-reset:hover { text-decoration: underline; }
.picker-list {
  list-style: none;
  margin: 0;
  padding: 8px 0;
  min-width: 180px;
  max-height: 320px;
  overflow-y: auto;
}
.picker-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
}
.picker-item:hover { background: var(--p-surface-100); }
.picker-label {
  font-size: 0.875rem;
  cursor: pointer;
  user-select: none;
}
</style>

<style>
/* Global: Aktionen-Menü Danger-Item (nicht scoped, damit es im Teleport-Popup greift) */
.action-menu-item {
  display: flex;
  align-items: center;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--p-text-color);
  text-decoration: none;
  transition: background 0.15s;
}
.action-menu-item:not(.p-disabled):hover {
  background: var(--p-surface-100);
}
.action-menu-item.p-disabled {
  opacity: 0.4;
  cursor: default;
  pointer-events: none;
}
.action-menu-item.action-item-danger {
  color: var(--p-red-500, #ef4444);
}
.action-menu-item.action-item-danger:not(.p-disabled):hover {
  background: color-mix(in srgb, var(--p-red-500, #ef4444) 8%, transparent);
}
</style>
